import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, FileText, Receipt, Layers, Upload, Zap, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Landing() {
  const { t } = useTranslation();

  const features = [
    { icon: Sparkles, key: "ai" },
    { icon: Layers, key: "tpl" },
    { icon: Upload, key: "logo" },
    { icon: Zap, key: "convert" },
    { icon: Receipt, key: "track" },
    { icon: FileText, key: "print" },
  ];

  const steps = [t("landing.step1"), t("landing.step2"), t("landing.step3"), t("landing.step4")];

  return (
    <div className="min-h-screen bg-surface-base text-white overflow-x-hidden">
      {/* NAV */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-2xl" data-testid="brand-link">
            <span className="text-gradient-vel7o">Vel7o</span><span className="text-white">.com</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <a href="#features" className="hover:text-brand-cyan transition-colors">{t("landing.section_features")}</a>
            <a href="#como" className="hover:text-brand-cyan transition-colors">{t("landing.section_how")}</a>
            <a href="#precios" className="hover:text-brand-cyan transition-colors">{t("landing.section_pricing")}</a>
          </nav>
          <div className="flex items-center gap-3">
            <LanguageSwitcher compact />
            <Link to="/login"><Button variant="ghost" data-testid="nav-login" className="text-white/80 hover:text-white hover:bg-white/5">{t("common.login")}</Button></Link>
            <Link to="/register"><Button data-testid="nav-register" className="bg-brand-orange hover:bg-brand-orange-hover text-white font-medium">{t("common.register")}</Button></Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-40 pb-24 px-6">
        <div className="absolute inset-0 -z-10">
          <img src="https://static.prod-images.emergentagent.com/jobs/e8272f7e-7832-47ed-96ae-700f837f3c77/images/6689817b4fde23fc49202b38e392fabf5a467e65255bfca0eb5f274650f7d044.png" alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-surface-base/60 via-surface-base/80 to-surface-base" />
        </div>
        <div className="max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-cyan/30 bg-brand-cyan/5 text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-6">
            <Sparkles className="w-3 h-3" /> {t("landing.badge")}
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            {t("landing.hero_title_1")} <br />
            {t("landing.hero_title_2")} <span className="text-gradient-vel7o">{t("landing.hero_title_3")}</span>.
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl leading-relaxed">
            {t("landing.hero_subtitle")}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to="/register">
              <Button data-testid="hero-cta" size="lg" className="bg-brand-orange hover:bg-brand-orange-hover text-white px-8 py-6 text-base font-medium">
                {t("landing.cta_start")} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/login">
              <Button data-testid="hero-login" variant="outline" size="lg" className="border-white/20 bg-transparent text-white hover:bg-white/5 px-8 py-6 text-base font-medium">
                {t("landing.cta_already")}
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-6 text-xs text-white/50">
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-cyan" /> {t("landing.no_card")}</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-cyan" /> {t("landing.templates_included")}</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-brand-cyan" /> {t("landing.your_language")}</div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="relative py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-16">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-3">{t("landing.section_features")}</div>
              <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tight max-w-2xl">{t("landing.features_title")}</h2>
            </div>
            <p className="text-white/60 max-w-sm">{t("landing.features_subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <div key={i} data-testid={`feature-${i}`} className="group p-8 rounded-lg border border-white/10 bg-[#0A1128]/40 hover:border-brand-cyan/40 hover:-translate-y-1 transition-all duration-300">
                <f.icon className="w-6 h-6 text-brand-cyan mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="font-display font-semibold text-lg mb-2">{t(`landing.feat_${f.key}_title`)}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{t(`landing.feat_${f.key}_text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW */}
      <section id="como" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-orange mb-3">{t("landing.section_how")}</div>
            <h2 className="font-display font-bold text-4xl lg:text-5xl tracking-tight mb-8">{t("landing.how_title")}</h2>
            <ol className="space-y-5">
              {steps.map((s, i) => (
                <li key={i} className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand-orange/10 border border-brand-orange/30 text-brand-orange font-display font-bold flex items-center justify-center">{i + 1}</div>
                  <div className="pt-1.5 text-white/80">{s}</div>
                </li>
              ))}
            </ol>
            <Link to="/register" className="inline-block mt-10">
              <Button data-testid="how-cta" size="lg" className="bg-brand-cyan hover:bg-brand-cyan-hover text-surface-base font-semibold">
                {t("landing.how_cta")} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="relative aspect-square rounded-lg overflow-hidden border border-white/10">
            <img src="https://images.pexels.com/photos/30349399/pexels-photo-30349399.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="freelancer workspace" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-surface-base via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="precios" className="py-24 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-3">{t("landing.section_pricing")}</div>
          <h2 className="font-display font-black text-5xl lg:text-6xl tracking-tight mb-6">
            {t("landing.pricing_title")}
          </h2>
          <p className="text-white/60 mb-10 text-lg">{t("landing.pricing_subtitle")}</p>
          <Link to="/register">
            <Button data-testid="footer-cta" size="lg" className="bg-brand-orange hover:bg-brand-orange-hover text-white px-10 py-6 text-base font-medium glow-orange">
              {t("landing.pricing_cta")} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 px-6 text-center text-xs text-white/40">
        <div className="flex justify-center gap-4 mb-3 flex-wrap">
          <Link to="/aviso-legal" className="hover:text-white transition-colors" data-testid="footer-aviso-legal">{t("landing.legal_notice")}</Link>
          <span className="text-white/20">·</span>
          <Link to="/privacidad" className="hover:text-white transition-colors" data-testid="footer-privacidad">{t("landing.privacy")}</Link>
          <span className="text-white/20">·</span>
          <Link to="/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies">{t("landing.cookies")}</Link>
        </div>
        © {new Date().getFullYear()} Vel7o.com · {t("landing.footer_made")}
      </footer>
    </div>
  );
}
